/**
 * qty-discount.js — DockGear Theme
 *
 * Quantity-based price discount widget for the `product.qty-discount` template.
 *
 * Differences from other themes:
 *   - Price block ID: `price-{sectionId}` (lowercase)
 *   - No pubsub variant event — price block innerHTML is replaced directly via fetch.
 *     A MutationObserver watches the price block and re-applies the discount after
 *     each variant swap, using the updated data-base-price from the re-rendered widget.
 *
 * TIERS — read from product metafield `custom.qty_tiers` (JSON).
 *   Set per-product in Admin → Products → [product] → Metafields.
 *   Format: [{ "minQty": 2, "maxQty": 4, "pct": 5 }, ...]
 *   The full-price row (qty 1) is always prepended automatically.
 */

(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Tier helpers
  // ---------------------------------------------------------------------------

  function getTiers() {
    const widget = getWidget();
    if (!widget) return [];
    try {
      const metafieldTiers = JSON.parse(widget.dataset.tiers || '[]');
      return [{ minQty: 1, maxQty: 1, pct: 0 }, ...metafieldTiers];
    } catch (e) {
      return [{ minQty: 1, maxQty: 1, pct: 0 }];
    }
  }

  function getTierForQty(qty) {
    const tiers = getTiers();
    for (let i = tiers.length - 1; i >= 0; i--) {
      if (qty >= tiers[i].minQty) return tiers[i];
    }
    return tiers[0];
  }

  // ---------------------------------------------------------------------------
  // Money formatter
  // ---------------------------------------------------------------------------

  function formatMoney(cents) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(
        cents,
        window.Shopify.money_format || '${{amount}}'
      );
    }
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ---------------------------------------------------------------------------
  // DOM queries — always fresh
  // ---------------------------------------------------------------------------

  function getWidget()     { return document.getElementById('qty-discount-widget'); }
  function getSectionId()  { const w = getWidget(); return w ? w.dataset.sectionId : null; }

  function getQtyInput() {
    const sectionId = getSectionId();
    if (!sectionId) return null;
    const form = document.getElementById('product-form-' + sectionId);
    return form ? form.querySelector('[name="quantity"]') : null;
  }

  function getPriceBlock() {
    const sectionId = getSectionId();
    return sectionId ? document.getElementById('price-' + sectionId) : null;
  }

  function getBasePriceCents() {
    const w = getWidget();
    return w ? (parseInt(w.dataset.basePrice, 10) || 0) : 0;
  }

  // ---------------------------------------------------------------------------
  // Price DOM manipulation
  // ---------------------------------------------------------------------------

  function applyPriceDiscount(discountPct) {
    const priceBlock     = getPriceBlock();
    const basePriceCents = getBasePriceCents();
    if (!priceBlock || !basePriceCents) return;

    let overlay = priceBlock.parentNode.querySelector('.qty-discount-price-overlay');

    if (discountPct === 0) {
      priceBlock.style.display = '';
      const existing = priceBlock.parentNode.querySelector('.qty-discount-price-overlay');
      if (existing) existing.remove();
      return;
    }

    const discountedCents = Math.round(basePriceCents * (1 - discountPct / 100));

    priceBlock.style.display = 'none';

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.className = 'qty-discount-price-overlay';
      overlay.style.cssText = 'font-size:inherit;font-weight:700;line-height:1.2;margin-bottom:0.5rem;';
      priceBlock.parentNode.insertBefore(overlay, priceBlock);
    }

    overlay.innerHTML =
      '<s style="color:#cc0000;font-size:0.85em;font-weight:400;opacity:0.85;margin-right:0.4em;">' +
        formatMoney(basePriceCents) +
      '</s>' +
      '<span>' + formatMoney(discountedCents) + '</span>';
  }

  function updateTable(activeTier) {
    const widget = getWidget();
    if (!widget) return;

    const basePriceCents = getBasePriceCents();

    widget.querySelectorAll('.qty-discount-row').forEach((row) => {
      const min    = parseInt(row.dataset.minQty, 10);
      const max    = parseInt(row.dataset.maxQty, 10);
      const pct    = parseInt(row.dataset.pct, 10);
      const active = activeTier.minQty === min && activeTier.maxQty === max;

      row.classList.toggle('qty-discount-row--active', active);

      const priceCell = row.querySelector('.qty-discount-price');
      if (priceCell && basePriceCents) {
        priceCell.textContent = formatMoney(Math.round(basePriceCents * (1 - pct / 100)));
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Core update
  // ---------------------------------------------------------------------------

  function update() {
    const qtyInput = getQtyInput();
    const qty      = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    const tier     = getTierForQty(qty);
    applyPriceDiscount(tier.pct);
    updateTable(tier);
  }

  function wireQtyInput() {
    const qtyInput = getQtyInput();
    if (!qtyInput || qtyInput._qtyDiscountWired) return;
    qtyInput._qtyDiscountWired = true;
    qtyInput.addEventListener('change', update);
    qtyInput.addEventListener('input',  update);
  }

  // ---------------------------------------------------------------------------
  // MutationObserver — detects when the price block innerHTML is replaced
  // (dockgear has no pubsub variant event; it swaps innerHTML directly)
  // ---------------------------------------------------------------------------

  function watchPriceBlock(sectionId) {
    const priceBlock = document.getElementById('price-' + sectionId);
    if (!priceBlock) return;

    let debounceTimer;

    const observer = new MutationObserver(() => {
      // Ignore mutations caused by our own overlay logic
      if (priceBlock.style.display === 'none') return;
      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        wireQtyInput();
        update();
      }, 50);
    });

    observer.observe(priceBlock, { childList: true, subtree: true });
  }

  // ---------------------------------------------------------------------------
  // Boot
  // ---------------------------------------------------------------------------

  function init() {
    if (!getWidget()) return;

    const sectionId = getSectionId();
    wireQtyInput();
    update();
    watchPriceBlock(sectionId);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
